import React, { useEffect } from 'react';
import { Form, Input, InputNumber } from 'antd';
import EditableCard from 'components/EditableCard';
import { WarehouseType } from 'graphql/types/WarehouseType';
import { GeoType } from 'graphql/types/GeoType';
import CascadingAddressSelector from 'components/CascadingAddressSelector';
import { useTranslation } from 'react-i18next';
import { useShouldUseVerticalLayout } from '../../../../utils/languageUtils';

type SiteAddressFormValues = WarehouseType & {
    latitude?: number;
    longitude?: number;
    locationString?: string;
};

interface SiteAddressInfoFormProps {
    data: WarehouseType;
    isEditing?: boolean;
    isNew?: boolean;
    onEdit?: () => void;
    onSave?: (data: WarehouseType) => void;
    onCancel?: () => void;
    title?: string;
    loading?: boolean;
    bordered?: boolean;
}

const SiteAddressInfoForm: React.FC<SiteAddressInfoFormProps> = ({
    data,
    isEditing = false,
    isNew = false,
    onEdit,
    onSave,
    onCancel,
    title = 'Address Info',
    loading = false,
    bordered = false,
}) => {
    const [form] = Form.useForm<SiteAddressFormValues>();
    const { t } = useTranslation();
    const useVerticalLayout = useShouldUseVerticalLayout();
    const locationString = Form.useWatch('locationString', form);

    const resetFormData = () => {
        form.setFieldsValue({
            addressLine1: data.addressLine1,
            addressLine2: data.addressLine2,
            country: data.country || '',
            state: data.state || '',
            city: data.city || '',
            zipcode: data.zipcode || '',
            timezone: data.timezone || '',
            latitude: data.location?.latitude,
            longitude: data.location?.longitude,
            locationString: data.location
                ? `${data.location.latitude}, ${data.location.longitude}`
                : '',
        });
    };

    useEffect(() => {
        resetFormData();
    }, [data, form]);

    useEffect(() => {
        if (!isEditing) {
            resetFormData();
        }
    }, [isEditing, data]);

    useEffect(() => {
        if (!locationString || (!isEditing && !isNew)) {
            return;
        }
        const parts = locationString.split(',').map((value) => Number(value.trim()));
        if (parts.length === 2 && !Number.isNaN(parts[0]) && !Number.isNaN(parts[1])) {
            form.setFieldsValue({
                latitude: parts[0],
                longitude: parts[1],
            });
        }
    }, [locationString, isEditing, isNew, form]);

    const canEditField = (fieldName: string) => {
        if (isNew) return true;
        if (!isEditing) return false;
        return [
            'addressLine1',
            'addressLine2',
            'country',
            'state',
            'city',
            'zipcode',
            'latitude',
            'longitude',
        ].includes(fieldName);
    };

    const showCascadingAddress = isNew || isEditing;

    const parseLocation = (latitude?: number, longitude?: number): GeoType | undefined => {
        if (latitude === undefined || latitude === null || longitude === undefined || longitude === null) {
            return undefined;
        }
        return { latitude, longitude };
    };

    const handleSave = async () => {
        try {
            await form.validateFields();
        } catch {
            return;
        }

        const latitude = form.getFieldValue('latitude');
        const longitude = form.getFieldValue('longitude');

        if (isNew) {
            const values = form.getFieldsValue();
            onSave?.({
                ...values,
                location: parseLocation(latitude, longitude),
            } as WarehouseType);
            return;
        }

        onSave?.({
            id: data.id,
            addressLine1: form.getFieldValue('addressLine1'),
            addressLine2: form.getFieldValue('addressLine2'),
            country: form.getFieldValue('country'),
            state: form.getFieldValue('state'),
            city: form.getFieldValue('city'),
            zipcode: form.getFieldValue('zipcode'),
            timezone: form.getFieldValue('timezone'),
            location: parseLocation(latitude, longitude),
        } as WarehouseType);
    };

    return (
        <EditableCard
            title={title}
            isEditing={isEditing}
            onEdit={onEdit}
            onSave={handleSave}
            onCancel={onCancel}
            loading={loading}
            bordered={bordered}
        >
            <Form
                layout={useVerticalLayout ? 'vertical' : 'horizontal'}
                form={form}
                colon={false}
                labelCol={useVerticalLayout ? undefined : { span: 7 }}
                wrapperCol={useVerticalLayout ? undefined : { span: 17 }}
                className="[&_.ant-form-item-control-input]:w-full [&_.ant-input]:w-full [&_.ant-select]:w-full [&_.ant-select-selector]:w-full [&_.ant-form-item-control-input-content]:w-full [&_.ant-form-item-control]:w-full [&_.ant-form-item-label]:text-left [&_.ant-form-item-label>label]:text-left"
            >
                <Form.Item name="locationString" hidden>
                    <Input />
                </Form.Item>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-10 gap-y-4 mb-4 [&_.ant-form-item-control-input-content]:w-full [&_.ant-form-item-control]:w-full [&_.ant-form-item]:mb-0">
                    <Form.Item
                        label={<span className="font-bold">{t('central.sites.details.addr1')}</span>}
                        name="addressLine1"
                        rules={isEditing || isNew ? [{ required: true, message: 'Please enter address line 1' }] : []}
                    >
                        <Input disabled={!canEditField('addressLine1')} />
                    </Form.Item>
                    <Form.Item
                        label={<span className="font-bold">{t('central.sites.details.addr2')}</span>}
                        name="addressLine2"
                    >
                        <Input disabled={!canEditField('addressLine2')} />
                    </Form.Item>
                    {showCascadingAddress ? (
                        <div className="lg:col-span-2">
                            <CascadingAddressSelector
                                form={form}
                                layout="alignedRow"
                                addressLabel={<span className="font-bold">{t('central.sites.details.address')}</span>}
                                countryWidth="20%"
                                stateWidth="20%"
                                cityWidth="30%"
                                zipcodeWidth="30%"
                                autoFillGeoLocation={true}
                                geoLocationFieldName="locationString"
                                initialCountry={data.country}
                                initialState={data.state}
                                initialCity={data.city}
                                initialZipcode={data.zipcode}
                            />
                        </div>
                    ) : (
                        <>
                            <Form.Item
                                label={<span className="font-bold">{t('central.sites.details.country')}</span>}
                                name="country"
                            >
                                <Input disabled />
                            </Form.Item>
                            <Form.Item
                                label={<span className="font-bold">{t('central.sites.details.state')}</span>}
                                name="state"
                            >
                                <Input disabled />
                            </Form.Item>
                            <Form.Item
                                label={<span className="font-bold">{t('central.sites.details.city')}</span>}
                                name="city"
                            >
                                <Input disabled />
                            </Form.Item>
                            <Form.Item
                                label={<span className="font-bold">{t('central.sites.details.zipcode')}</span>}
                                name="zipcode"
                            >
                                <Input disabled />
                            </Form.Item>
                        </>
                    )}
                    <Form.Item
                        label={<span className="font-bold">{t('central.sites.details.timezone')}</span>}
                        name="timezone"
                    >
                        <Input
                            placeholder={t('central.sites.placeholders.autoFilledFromZipcode') as string}
                            disabled
                        />
                    </Form.Item>
                    {showCascadingAddress ? (
                        <>
                            <Form.Item
                                label={<span className="font-bold">{t('central.sites.details.latitude')}</span>}
                                name="latitude"
                                rules={isEditing || isNew ? [{ required: true, message: 'Please enter latitude' }] : []}
                            >
                                <InputNumber
                                    className="w-full"
                                    disabled={!canEditField('latitude')}
                                    placeholder={t('central.sites.placeholders.autoFilledFromZipcode') as string}
                                />
                            </Form.Item>
                            <Form.Item
                                label={<span className="font-bold">{t('central.sites.details.longitude')}</span>}
                                name="longitude"
                                rules={isEditing || isNew ? [{ required: true, message: 'Please enter longitude' }] : []}
                            >
                                <InputNumber
                                    className="w-full"
                                    disabled={!canEditField('longitude')}
                                    placeholder={t('central.sites.placeholders.autoFilledFromZipcode') as string}
                                />
                            </Form.Item>
                        </>
                    ) : (
                        <Form.Item
                            label={<span className="font-bold">{t('central.sites.details.geoCoding')}</span>}
                        >
                            <Input
                                value={
                                    data.location
                                        ? `${data.location.latitude}, ${data.location.longitude}`
                                        : ''
                                }
                                disabled
                            />
                        </Form.Item>
                    )}
                </div>
            </Form>
        </EditableCard>
    );
};

export default SiteAddressInfoForm;
